import { centsToDollars, dollarsToCents } from '@audience-republic/audience-manager-shared/utils/currency'
import { clone } from '@/utils/helpers'

export function fanFilterWithExtraConditions(filter, conditions, logic) {
  const resFilter = clone(filter || { conditions: [], logic: [] })

  const searchStringFilter = {
    conditions: conditions,
    logic: logic,
  }

  if (resFilter.conditions.length === 0) {
    return searchStringFilter
  }
  else {
    return {
      conditions: resFilter.conditions.concat(searchStringFilter.conditions),
      logic: resFilter.logic.concat(['and']).concat(searchStringFilter.logic),
    }
  }
}

export function mergeSearchStringToOrderFilters(filter, searchString) {
  const resFilter = clone(filter || { conditions: [], logic: [] })

  if (!searchString) {
    return resFilter
  }

  // Split by space, also remove all empty string from list of separated values
  const [firstPart, secondPart] = searchString
    .split(' ')
    .filter(val => !!val)
  const nameSeparated = !!(firstPart && secondPart)

  /**
   * CAUTION!!!!
   * 2019.10.11
   * Our audienceFilter will group all conditions with 'or' between as one 'logic group',
   * so "a and b and c or d or e and f" will be "a and b and (c or d or e) and f",
   * but here, we're trying to attach (firstName or lastName or emailAddress or mobileNumber),
   * which is fine because it has only "ors", which won't break them into two groups.
   * But it will break if we're trying to attach (firstName and lastName or emailAddress or mobileNumber),
   * it will end up with (firstName and lastName) or emailAddress or mobileNumber
   * For more details: https://trello.com/c/N6Q8QvgL/1346-audiencefilter-logic-group
   */
  if (nameSeparated) {
    return fanFilterWithExtraConditions(
      filter,
      [
        {
          name: 'firstName',
          type: 'text',
          data: { value: firstPart, condition: 'contains' },
        },
        {
          name: 'lastName',
          type: 'text',
          data: { value: secondPart, condition: 'contains' },
        },
      ],
      ['and'],
    )
  }
  else {
    return fanFilterWithExtraConditions(
      filter,
      [
        {
          name: 'firstName',
          type: 'text',
          data: { value: searchString, condition: 'contains' },
        },
        {
          name: 'lastName',
          type: 'text',
          data: { value: searchString, condition: 'contains' },
        },
      ],
      ['or', 'or', 'or'],
    )
  }
}

export function mergeSearchStringToFanFilters(filter, searchString) {
  const resFilter = clone(filter || { conditions: [], logic: [] })

  if (!searchString) {
    return resFilter
  }

  // Split by space, also remove all empty string from list of separated values
  const [firstPart, secondPart] = searchString
    .split(' ')
    .filter(val => !!val)
  const nameSeparated = !!(firstPart && secondPart)

  /**
   * CAUTION!!!!
   * 2019.10.11
   * Our audienceFilter will group all conditions with 'or' between as one 'logic group',
   * so "a and b and c or d or e and f" will be "a and b and (c or d or e) and f",
   * but here, we're trying to attach (firstName or lastName or emailAddress or mobileNumber),
   * which is fine because it has only "ors", which won't break them into two groups.
   * But it will break if we're trying to attach (firstName and lastName or emailAddress or mobileNumber),
   * it will end up with (firstName and lastName) or emailAddress or mobileNumber
   * For more details: https://trello.com/c/N6Q8QvgL/1346-audiencefilter-logic-group
   */
  if (nameSeparated) {
    return fanFilterWithExtraConditions(
      filter,
      [
        {
          name: 'firstName',
          type: 'text',
          data: { value: firstPart, condition: 'contains' },
        },
        {
          name: 'lastName',
          type: 'text',
          data: { value: secondPart, condition: 'contains' },
        },
      ],
      ['and'],
    )
  }
  else {
    return fanFilterWithExtraConditions(
      filter,
      [
        {
          name: 'firstName',
          type: 'text',
          data: { value: searchString, condition: 'contains' },
        },
        {
          name: 'lastName',
          type: 'text',
          data: { value: searchString, condition: 'contains' },
        },
        {
          name: 'emailAddress',
          type: 'text',
          data: { value: searchString, condition: 'contains' },
        },
        {
          name: 'mobileNumber',
          type: 'text',
          data: { condition: 'contains', value: searchString },
        },
      ],
      ['or', 'or', 'or'],
    )
  }
}

// Applies `transformValue` to the numeric value(s) of a single `currency`-typed
// condition only. `numeric` conditions (and every other type) are returned
// untouched — `numeric` and `currency` share the same input path
// (AudienceFilterCondition.vue), so this gate must stay strict.
function transformCurrencyConditionValues(condition, transformValue) {
  if (condition?.type !== 'currency' || !condition.data) {
    return condition
  }

  const data = { ...condition.data }

  if (typeof data.value === 'number') {
    data.value = transformValue(data.value)
  }

  if (Array.isArray(data.values)) {
    data.values = data.values.map(value =>
      typeof value === 'number' ? transformValue(value) : value,
    )
  }

  return { ...condition, data }
}

// Outbound: converts `currency`-typed condition values from dollars to cents,
// ahead of sending the filter to the API. This is the single outbound
// serialization chokepoint (called from segment/audience store actions).
export function convertCurrencyFilterToMinorUnits(filter) {
  const clonedFilter = clone(filter || { conditions: [], logic: [] })
  clonedFilter.conditions = (clonedFilter.conditions || []).map(condition =>
    transformCurrencyConditionValues(condition, dollarsToCents),
  )
  return clonedFilter
}

// Inbound: converts `currency`-typed condition values from cents back to
// dollars, the moment a saved segment's filter arrives from the API. Mirror
// image of convertCurrencyFilterToMinorUnits — this is the single inbound
// conversion point (called from the segment FETCH store actions in
// client/store/modules/segment/actions.ts). NOT called from
// loadFiltersFromObject, which must stay unit-agnostic to avoid re-entrancy.
export function convertCurrencyFilterToMajorUnits(filter) {
  const clonedFilter = clone(filter || { conditions: [], logic: [] })
  clonedFilter.conditions = (clonedFilter.conditions || []).map(condition =>
    transformCurrencyConditionValues(condition, centsToDollars),
  )
  return clonedFilter
}

export function filterCriteriaHasConstraint(filterCriteria) {
  if (!filterCriteria) return

  return (
    !!filterCriteria.constraints
    // @ts-ignore
    && (filterCriteria.constraints?.event?.enabled
      // @ts-ignore
      || filterCriteria.constraints?.campaign?.enabled
      // @ts-ignore
      || filterCriteria.constraints?.message?.enabled
      // @ts-ignore
      || filterCriteria.constraints?.period?.enabled
      // @ts-ignore
      || filterCriteria.constraints?.loyaltyProgram?.enabled
      // @ts-ignore
      || filterCriteria.constraints?.loyaltyTier?.enabled)
  )
}

export function filterCriteriaHasMandatoryConstraint(filterCriteria) {
  if (!filterCriteria) return

  return (
    !!filterCriteria.constraints
    // @ts-ignore
    && ((filterCriteria.constraints?.event?.enabled
      && filterCriteria.constraints?.event?.mandatory)
    // @ts-ignore
    || (filterCriteria.constraints?.campaign?.enabled
      && filterCriteria.constraints?.campaign?.mandatory)
    // @ts-ignore
    || (filterCriteria.constraints?.message?.enabled
      && filterCriteria.constraints?.message?.mandatory)
    // @ts-ignore
    || (filterCriteria.constraints?.period?.enabled
      && filterCriteria.constraints?.period?.mandatory)
    // @ts-ignore
    || (filterCriteria.constraints?.loyaltyProgram?.enabled
      && filterCriteria.constraints?.loyaltyProgram?.mandatory)
    // @ts-ignore
    || (filterCriteria.constraints?.loyaltyTier?.enabled
      && filterCriteria.constraints?.loyaltyTier?.mandatory))
  )
}

// Returns TRUE if a given filterCondition's mandatory constraints are fulfilled in the filterCriteria
// Also returns true if no mandatory constraints exist
export function filterCriteriaMandatoryConstraintsFilled(
  filterCriteria,
  editFilterCondition,
) {
  if (!filterCriteria.constraints) return true
  const constraintKeys = Object.keys(filterCriteria.constraints).filter(
    (item) => {
      return filterCriteria.constraints[item].mandatory
    },
  )
  let isFulfilled = true
  constraintKeys.forEach((constraintName) => {
    if (
      !editFilterCondition.constraints[constraintName]
      || !editFilterCondition.constraints[constraintName].condition
    ) {
      isFulfilled = false
    }
  })
  return isFulfilled
}

export function filterCriteriaMessageConstraintsFilled(
  filterCriteria,
  editFilterCondition,
) {
  if (!filterCriteria.constraints) return true
  const constraintMandatory
    = filterCriteria?.constraints?.message?.enabled
      && filterCriteria?.constraints?.message?.mandatory
  if (!constraintMandatory) return true
  const constraintCondition
    = editFilterCondition?.constraints?.message?.condition
  const constraintValues = editFilterCondition?.constraints?.message?.values
  if (!constraintCondition) return false
  if (!constraintValues || constraintValues.length === 0) return false
  return true
}
